import { ReactNode, createContext, useContext, useCallback, useState, useMemo, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthService } from '../services/auth.service';
import { AuthCredentials, AuthResponse, SSOCredentials } from '@models/Auth';
import jwt from 'jwt-decode';
import { StorageManager } from '@storage/index';
import { User } from '@models/User';

interface AuthContextData {
	isAuth: boolean;
	logout: () => void;
	loginCredentials: ({ username, password }: AuthCredentials) => Promise<string | void>;
	loginSSO: (credentials: SSOCredentials) => Promise<string | void>;
	redirect: (path: string) => void;
}

interface AuthProviderProps {
	children: ReactNode;
}

const AuthContext = createContext({} as AuthContextData);

export const AuthProvider = ({ children }: AuthProviderProps) => {
	const authService = useMemo(() => new AuthService(), []);

	const storage = useMemo(() => new StorageManager(), []);
	const localStorage = useMemo(() => new StorageManager('local'), []);

	const navigate = useNavigate();
	const [token, setToken] = useState<string | undefined>(storage.getItem<AuthResponse>('credentials')?.accessToken);

	const handleData = useCallback(
		(data: AuthResponse, rememberMe = false) => {
			if (!token) {
				const decodedUser: User = jwt(data.accessToken);

				storage.setItem({ key: 'credentials', value: data });

				if (rememberMe) localStorage.setItem({ key: 'credentials', value: data });

				if (decodedUser.IDPERLET && decodedUser.CODPERLET) {
					const perletIds = decodedUser.IDPERLET.split(';');
					const perletCodes = decodedUser.CODPERLET.split(';');

					decodedUser.PERLETIDS = perletIds;
					decodedUser.PERLETCODS = perletCodes;

					decodedUser.IDPERLET = '';
					decodedUser.CODPERLET = '';
				}

				storage.setItem({ key: 'user', value: decodedUser });

				setToken(data.accessToken);
			}
		},
		[localStorage, storage, token],
	);

	const handleAuthCredentials = useCallback(
		async ({ username, password, rememberMe }: AuthCredentials) => {
			const { data } = await authService.authCredentials(username.trim(), password);
			handleData(data, rememberMe);
		},
		[authService, handleData],
	);

	const handleAuthSSO = useCallback(
		async (credentials: SSOCredentials) => {
			const { data } = await authService.authSSO(credentials);
			handleData(data);
		},
		[authService, handleData],
	);

	const handleLogout = useCallback(() => {
		storage.clean();
		setToken(undefined);
	}, [storage]);

	const isAuth = useMemo(() => !!token, [token]);

	const redirect = useCallback(
		(path: string) => {
			navigate(path);
		},
		[navigate],
	);

	useEffect(() => {
		if (!isAuth) {
			const localCredentials = localStorage.getItem<AuthResponse>('credentials');
			if (localCredentials) {
				handleData(localCredentials, true);
			}
		}
	}, [handleData, isAuth, localStorage]);

	return (
		<AuthContext.Provider
			value={{
				isAuth,
				loginCredentials: handleAuthCredentials,
				loginSSO: handleAuthSSO,
				logout: handleLogout,
				redirect,
			}}
		>
			{children}
		</AuthContext.Provider>
	);
};

export const useAuthContext = () => useContext(AuthContext);
